import { Key, memo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Autocomplete } from '@ds';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';
import { useInfiniteTXCentersProvidersListQuery } from '@/queries/txcenter-providers';
import { renderAutocompleteItem } from '@/utils/helpers';
import { DriverFormType } from '@/api/apis/resources';

type TxCenterProviderAutocompleteProps = {
  isDisabled?: boolean;
};

export const TxCenterProviderAutocomplete = memo(({ isDisabled }: TxCenterProviderAutocompleteProps) => {
  const { control, setValue } = useFormContext<DriverFormType>();
  const txCenterProviderName = useWatch({ control, name: 'txCenterProviderName' });

  const { searchValue, list, isLoading, onSearchValue, setAutocompleteOpen, itemsScrollerRef, onItemSelectChange } =
    useInfiniteDropdown({
      setValue: (value: Key | null) => {
        setValue('txCenterProviderId', value?.toString() ?? '');
        const provider = list.find((m) => m.value === value?.toString());
        setValue('txCenterProviderName', provider ? provider.name : '');
      },
      listQuery: useInfiniteTXCentersProvidersListQuery,
      initialName: txCenterProviderName,
      defaultSortOrder: { orderBy: 'name', asc: true },
    });

  return (
    <Controller
      name="txCenterProviderId"
      control={control}
      render={({ field: { value }, fieldState: { invalid, error } }) => (
        <Autocomplete
          allowsCustomValue={false}
          placeholder="TX Center Provider name"
          aria-label="Select TX Center Provider"
          inputValue={searchValue}
          scrollRef={itemsScrollerRef}
          items={list}
          isLoading={isLoading}
          onInputChange={onSearchValue}
          isInvalid={invalid}
          isDisabled={isDisabled}
          errorMessage={error?.message}
          onOpenChange={setAutocompleteOpen}
          selectedKey={value || null}
          onSelectionChange={onItemSelectChange}
        >
          {renderAutocompleteItem}
        </Autocomplete>
      )}
    />
  );
});
